(() => {
  if (customElements.get('rm-volume-discount')) return;

  customElements.define('rm-volume-discount', class RMVolumeDiscount extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.currency = this.dataset.currency || 'USD';
      this.locale = this.dataset.locale || 'en-US';
      this.setQuantityOnClick = this.dataset.setQuantity !== 'false';
      this.variantId = String(this.dataset.currentVariantId || '');
      this.money = new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency });
      this.tiers = [...this.querySelectorAll('[data-rmvd-tier]')];
      this.variants = this.readVariants();
      this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`);
      this.unsubscribeVariant = null;
    }

    connectedCallback() {
      this.bindTierClicks();
      this.bindQuantity();
      this.bindVariantChanges();
      this.renderPrices();
      this.syncSelectedTier();
    }

    disconnectedCallback() {
      if (this.unsubscribeVariant) this.unsubscribeVariant();
    }

    readVariants() {
      const node = this.querySelector('[data-rmvd-variants]');
      if (!node) return [];
      try { return JSON.parse(node.textContent); }
      catch (error) { console.warn('Volume discount variant data could not be read.', error); return []; }
    }

    get currentPrice() {
      const variant = this.variants.find((item) => String(item.id) === String(this.variantId));
      return Number(variant?.price || 0);
    }

    formatMoney(cents) {
      return this.money.format((Number(cents) || 0) / 100);
    }

    discountedPrice(price, discount) {
      return Math.round(Number(price) * (1 - (Number(discount) || 0) / 100));
    }

    renderPrices() {
      const price = this.currentPrice;
      this.tiers.forEach((tier) => {
        const discount = Number(tier.dataset.discount) || 0;
        const finalPrice = this.discountedPrice(price, discount);
        const priceNode = tier.querySelector('[data-rmvd-price]');
        const originalNode = tier.querySelector('[data-rmvd-original]');
        if (priceNode) priceNode.textContent = this.formatMoney(finalPrice);
        if (originalNode) originalNode.textContent = this.formatMoney(price);
      });
    }

    bindTierClicks() {
      this.tiers.forEach((tier) => {
        tier.addEventListener('click', () => {
          const quantity = Number(tier.dataset.quantity) || 1;
          if (this.setQuantityOnClick && this.quantityInput) {
            this.quantityInput.value = quantity;
            this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
            this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          this.syncSelectedTier(quantity);
        });
      });
    }

    bindQuantity() {
      if (!this.quantityInput) return;
      const sync = () => this.syncSelectedTier(Number(this.quantityInput.value) || 1);
      this.quantityInput.addEventListener('input', sync);
      this.quantityInput.addEventListener('change', sync);
    }

    bindVariantChanges() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId && String(event.data.sectionId) !== String(this.sectionId)) return;
          const variant = event?.data?.variant;
          if (!variant?.id) return;
          this.variantId = String(variant.id);
          if (!this.variants.some((item) => String(item.id) === this.variantId) && variant.price != null) {
            this.variants.push({ id: variant.id, price: variant.price });
          }
          this.renderPrices();
        });
      }
    }

    syncSelectedTier(forcedQuantity) {
      const quantity = Number(forcedQuantity ?? this.quantityInput?.value ?? 1) || 1;
      const eligible = this.tiers
        .filter((tier) => quantity >= (Number(tier.dataset.quantity) || 1))
        .sort((a, b) => (Number(b.dataset.quantity) || 1) - (Number(a.dataset.quantity) || 1));
      const selected = eligible[0] || this.tiers[0];
      this.tiers.forEach((tier) => {
        const active = tier === selected;
        tier.classList.toggle('is-selected', active);
        tier.setAttribute('aria-pressed', String(active));
      });
    }
  });
})();
